import { SubscriptionTier } from '../hooks/useSubscription';
import type { EntitlementKey } from './entitlements';

// Define all available features
export type FeatureKey = 
  // Cases
  | 'case1' 
  | 'case2' 
  | 'case3' 
  | 'case4' 
  | 'case5' 
  | 'case6' 
  | 'case7' 
  | 'case8' 
  | 'case9' 
  | 'case10'
  | 'case11'
  | 'case12'
  | 'case13'
  | 'case14'
  | 'case15'
  | 'case16'
  | 'case17'
  // Ecosystem Solver Features
  | 'ecosystemSolverManualEntry'
  | 'ecosystemSolverSnapAndSolve'
  | 'seaWolfSolver'
  | 'seaWolfSolverManualEntry'
  // Add other features here as needed
  ;

// Map features to their minimum required tier
export const featureAccessMap: Record<FeatureKey, SubscriptionTier> = {
  // Free tier features
  case1: 'free',
  case10: 'free',
  case17: 'free',

  // Red Rock practice library: free with sign-in (no paid tier)
  case2: 'free',
  case3: 'free',
  case4: 'free',
  case5: 'free',
  case6: 'free',
  case7: 'free',
  case8: 'free',
  case9: 'free',
  case11: 'free',
  case12: 'free',
  case13: 'free',
  case14: 'free',
  case15: 'free',
  case16: 'free',
  ecosystemSolverManualEntry: 'basic',
  seaWolfSolverManualEntry: 'basic',

  // Pro tier features (includes all features)
  ecosystemSolverSnapAndSolve: 'pro',
  seaWolfSolver: 'pro',
  // Add other pro features here
};

// ---------------------------------------------------------------------------
// Tier → entitlements (shared by ProtectedRoute and route-level gating)
// Single source of truth so client and server stay aligned (server/entitlements.js mirrors these).
// ---------------------------------------------------------------------------
export const BASIC_ENTITLEMENTS: readonly EntitlementKey[] = [
  'sea_wolf_solver',
  'sea_wolf_simulation',
  'red_rock_simulation',
];
export const PRO_ENTITLEMENTS: readonly EntitlementKey[] = [
  'mckinsey_ai_coach',
  'bcg_ai_coach',
];

/** Entitlements required for each route tier; having ANY grants access. */
export const TIER_REQUIRED_ENTITLEMENTS: Record<'free' | 'basic' | 'pro', readonly EntitlementKey[]> = {
  free: [],
  basic: BASIC_ENTITLEMENTS,
  pro: PRO_ENTITLEMENTS,
};

// ---------------------------------------------------------------------------
// Feature → required entitlements (canonical; used for entitlement-first gating)
// Having ANY of the listed entitlements grants access. Empty = no entitlement required (free).
// ---------------------------------------------------------------------------
const ASSESSMENT_ENTITLEMENTS = BASIC_ENTITLEMENTS;
const COACH_ENTITLEMENTS = PRO_ENTITLEMENTS;

export const FEATURE_REQUIRED_ENTITLEMENTS: Record<FeatureKey, readonly EntitlementKey[]> = {
  case1: [],
  case2: ASSESSMENT_ENTITLEMENTS,
  case3: ASSESSMENT_ENTITLEMENTS,
  case4: ASSESSMENT_ENTITLEMENTS,
  case5: ASSESSMENT_ENTITLEMENTS,
  case6: ASSESSMENT_ENTITLEMENTS,
  case7: ASSESSMENT_ENTITLEMENTS,
  case8: ASSESSMENT_ENTITLEMENTS,
  case9: ASSESSMENT_ENTITLEMENTS,
  case10: [],
  case11: COACH_ENTITLEMENTS,
  case12: COACH_ENTITLEMENTS,
  case13: COACH_ENTITLEMENTS,
  case14: COACH_ENTITLEMENTS,
  case15: COACH_ENTITLEMENTS,
  case16: COACH_ENTITLEMENTS,
  case17: [],
  ecosystemSolverManualEntry: ASSESSMENT_ENTITLEMENTS,
  ecosystemSolverSnapAndSolve: ASSESSMENT_ENTITLEMENTS,
  seaWolfSolver: ASSESSMENT_ENTITLEMENTS,
  seaWolfSolverManualEntry: ASSESSMENT_ENTITLEMENTS,
};

/** Check if user has any of the required entitlements for the feature (entitlement-first gating). */
export function canAccessFeatureByEntitlements(
  feature: FeatureKey,
  userEntitlements: readonly EntitlementKey[]
): boolean {
  const required = FEATURE_REQUIRED_ENTITLEMENTS[feature];
  if (required.length === 0) return true;
  return required.some((ent) => userEntitlements.includes(ent));
}

// Helper function to check if a user can access a feature (tier-based; deprecated after cutover)
export const canAccessFeature = (feature: FeatureKey, userTier: SubscriptionTier): boolean => {
  const requiredTier = featureAccessMap[feature];
  const tierOrder = { free: 0, basic: 1, pro: 2 };
  return tierOrder[userTier] >= tierOrder[requiredTier];
};

// Helper to get all features available for a tier
export const getFeaturesForTier = (tier: SubscriptionTier): FeatureKey[] => {
  return Object.entries(featureAccessMap)
    .filter(([_, requiredTier]) => canAccessFeature(_ as FeatureKey, tier))
    .map(([feature]) => feature as FeatureKey);
};

/** Canonical helper: derive available features from entitlements, not compatibility tier. */
export const getFeaturesForEntitlements = (
  userEntitlements: readonly EntitlementKey[]
): FeatureKey[] => {
  return (Object.keys(FEATURE_REQUIRED_ENTITLEMENTS) as FeatureKey[]).filter((feature) =>
    canAccessFeatureByEntitlements(feature, userEntitlements)
  );
};

/** Check if a list of entitlements satisfies a route tier (any of the tier's required entitlements). */
export function hasTierAccess(
  requiredTier: 'free' | 'basic' | 'pro',
  entitlements: readonly string[]
): boolean {
  if (requiredTier === 'free') return true;
  const required = TIER_REQUIRED_ENTITLEMENTS[requiredTier];
  if (!required?.length) return true;
  const list = entitlements ?? [];
  return required.some((e) => list.includes(e));
}

/** Optional upgrade CTA label per feature; used for locked-state messaging. */
export const FEATURE_UPGRADE_LABEL: Record<FeatureKey, string> = {
  case1: 'View plans',
  case2: 'View plans',
  case3: 'View plans',
  case4: 'View plans',
  case5: 'View plans',
  case6: 'View plans',
  case7: 'View plans',
  case8: 'View plans',
  case9: 'View plans',
  case10: 'View plans',
  case11: 'View plans',
  case12: 'View plans',
  case13: 'View plans',
  case14: 'View plans',
  case15: 'View plans',
  case16: 'View plans',
  case17: 'View plans',
  ecosystemSolverManualEntry: 'View plans',
  ecosystemSolverSnapAndSolve: 'View plans',
  seaWolfSolver: 'View plans',
  seaWolfSolverManualEntry: 'View plans',
}; 
